import { supabase } from './supabase';
import { checkAndHandleAuthError } from './authErrorHandler';

/**
 * Add a track to the user's favorites
 * @param userId - The user's ID
 * @param trackId - The track ID to add
 * @returns Promise with error if operation failed
 */
export async function addToFavorites(
  userId: string,
  trackId: string
): Promise<{ error: string | null }> {
  try {
    // Get current favorite tracks
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('favorite_tracks')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      return { error: fetchError.message };
    }

    // Get current favorites array (default to empty array)
    const currentFavorites: string[] = Array.isArray(profile?.favorite_tracks)
      ? (profile.favorite_tracks as string[])
      : [];

    // Check if track is already favorited
    if (currentFavorites.includes(trackId)) {
      return { error: null }; // Already favorited, no error
    }

    // Add track to favorites
    const updatedFavorites = [...currentFavorites, trackId];

    // Update profile
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ favorite_tracks: updatedFavorites })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating favorites:', updateError);
      return { error: updateError.message };
    }

    return { error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to add to favorites';
    console.error('Unexpected error adding to favorites:', err);
    return { error: errorMessage };
  }
}

/**
 * Remove a track from the user's favorites
 * @param userId - The user's ID
 * @param trackId - The track ID to remove
 * @returns Promise with error if operation failed
 */
export async function removeFromFavorites(
  userId: string,
  trackId: string
): Promise<{ error: string | null }> {
  try {
    // Get current favorite tracks
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('favorite_tracks')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      return { error: fetchError.message };
    }

    // Get current favorites array (default to empty array)
    const currentFavorites: string[] = Array.isArray(profile?.favorite_tracks)
      ? (profile.favorite_tracks as string[])
      : [];

    // Remove track from favorites
    const updatedFavorites = currentFavorites.filter((id) => id !== trackId);

    // Update profile
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ favorite_tracks: updatedFavorites })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating favorites:', updateError);
      return { error: updateError.message };
    }

    return { error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to remove from favorites';
    console.error('Unexpected error removing from favorites:', err);
    return { error: errorMessage };
  }
}

/**
 * Check if a track is in the user's favorites
 * @param userId - The user's ID
 * @param trackId - The track ID to check
 * @returns Promise with boolean indicating if track is favorited
 */
export async function isTrackFavorited(
  userId: string,
  trackId: string
): Promise<{ isFavorited: boolean; error: string | null }> {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('favorite_tracks')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return { isFavorited: false, error: error.message };
    }

    const favorites: string[] = Array.isArray(profile?.favorite_tracks)
      ? (profile.favorite_tracks as string[])
      : [];

    return { isFavorited: favorites.includes(trackId), error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to check favorites';
    console.error('Unexpected error checking favorites:', err);
    return { isFavorited: false, error: errorMessage };
  }
}

/**
 * Get all favorite track IDs for a user
 * @param userId - The user's ID
 * @returns Promise with array of track IDs
 */
export async function getFavoriteTracks(
  userId: string
): Promise<{ tracks: string[]; error: string | null }> {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('favorite_tracks')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      // Check if this is an auth error and redirect if needed
      if (checkAndHandleAuthError(error)) {
        return { tracks: [], error: 'Session expired' };
      }
      return { tracks: [], error: error.message };
    }

    const tracks: string[] = Array.isArray(profile?.favorite_tracks)
      ? (profile.favorite_tracks as string[])
      : [];

    return { tracks, error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to get favorites';
    console.error('Unexpected error getting favorites:', err);
    return { tracks: [], error: errorMessage };
  }
}

